import type { ParsedUrlQuery } from "node:querystring";
import type { ValueWithUnit } from "../node/type";
import type { CampaignType } from "../types/campaign";
import {
  getEventPageBaseUrl,
  getPromotionPageBaseUrl,
  getSurveyPageBaseUrl,
} from "./config";

export function getParamValue<T extends string = string>(
  query: ParsedUrlQuery,
  param: string
): T | null;
export function getParamValue<T extends string = string>(
  query: ParsedUrlQuery,
  param: string,
  defaultValue: T
): T;

/**
 * - URL 쿼리 파라미터에서 특정 파라미터의 값을 가져옵니다.
 * - 값이 없을 경우, defaultValue가 제공되면 해당 값을 반환하고, 그렇지 않으면 null을 반환합니다.
 * - defaultValue가 제공된 경우, 반환 타입은 T이며, 그렇지 않은 경우 T | null입니다.
 */
export function getParamValue<T extends string = string>(
  query: ParsedUrlQuery,
  param: string,
  defaultValue?: T
) {
  const value = query[param];
  if (!value) {
    return defaultValue || null;
  }

  return (Array.isArray(value) ? value[0] : value) as T;
}

/**
 * 1. http가 없으면 붙여줌
 * 2. URL의 끝에 "/"를 없앰
 */
export const getUrlWithSchema = (url: string) => {
  let ret = url.trim();

  if (ret.indexOf("http://") === -1 && ret.indexOf("https://") === -1) {
    ret = `http://${ret}`;
  }

  if (ret !== "http://" && ret[ret.length - 1] === "/") {
    ret = ret.substring(0, ret.length - 1);
  }

  return ret;
};

export const isValidURL = (url: string) => {
  const res = url.match(
    /(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)/g
  );
  return res !== null;
};

export const flatToQueryString = (
  paramArr:
    | Readonly<{ key: string; value: string }[]>
    | { key: string; value: string }[]
) =>
  `?${paramArr
    .filter(({ key }) => key.length > 0)
    .map(({ key, value }) => `${key}=${value}`)
    .join("&")}`;

export function getOptimizedImagePath(
  imagePath: string,
  isOptimize: boolean,
  hasTransparency?: boolean
) {
  const imageURL = new URL(imagePath);
  imageURL.searchParams.delete("f");

  if (isOptimize) {
    if (imageURL.pathname.endsWith("png") && !hasTransparency) {
      imageURL.searchParams.append("f", "jpg");
    }
  } else {
    imageURL.searchParams.append("f", "original");
  }

  return imageURL.toString();
}

const IMAGE_CDN_HOST = "image.codenbutter.com";
const RESIZABLE_IMAGE_EXTENSIONS = new Set(["jpg", "jpeg", "png"]);
const JPEG_IMAGE_FORMATS = new Set(["jpg", "jpeg"]);
const DISPLAY_IMAGE_RESIZE_DPR = 2;
const DISPLAY_IMAGE_RESIZE_STEP_PX = 100;
const MIN_DISPLAY_IMAGE_RESIZE_WIDTH_PX = 100;
const MAX_DISPLAY_IMAGE_RESIZE_WIDTH_PX = 1600;
const MIN_PNG_ORIGINAL_WIDTH_FOR_RESIZE_PX = 600;
const MAX_PNG_DISPLAY_RESIZE_WIDTH_PX = 400;

let displayImageResizeEnabled = true;

export const setDisplayImageResizeEnabled = (enabled: boolean) => {
  displayImageResizeEnabled = enabled;
};

const getImagePathExtension = (pathname: string) => {
  return pathname.split(".").pop()?.toLowerCase() ?? "";
};

const getDisplayImageResizeWidth = ({
  displayHeight,
  displayWidth,
  originalWidth,
}: {
  displayHeight?: ValueWithUnit;
  displayWidth?: ValueWithUnit;
  originalWidth?: number;
}) => {
  const displaySize = [displayWidth, displayHeight]
    .filter(
      (size): size is ValueWithUnit =>
        !!size && size.unit === "px" && size.value > 0
    )
    .map((size) => size.value)
    .sort((a, b) => b - a)[0];

  if (!displaySize) {
    return null;
  }

  const resizeWidth = Math.ceil(
    (displaySize * DISPLAY_IMAGE_RESIZE_DPR) / DISPLAY_IMAGE_RESIZE_STEP_PX
  ) * DISPLAY_IMAGE_RESIZE_STEP_PX;

  const cappedWidth = Math.min(
    Math.max(resizeWidth, MIN_DISPLAY_IMAGE_RESIZE_WIDTH_PX),
    MAX_DISPLAY_IMAGE_RESIZE_WIDTH_PX
  );

  if (originalWidth && cappedWidth >= originalWidth) {
    return null;
  }

  return cappedWidth;
};

export const getDisplayImagePath = ({
  displayHeight,
  path,
  fileType,
  displayWidth,
  originalWidth,
}: {
  displayHeight?: ValueWithUnit;
  path?: string;
  fileType?: string;
  displayWidth?: ValueWithUnit;
  originalWidth?: number;
}) => {
  if (!displayImageResizeEnabled) {
    return path;
  }

  if (!path) {
    return path;
  }

  let imageURL: URL;
  try {
    imageURL = new URL(path);
  } catch {
    return path;
  }

  if (imageURL.hostname !== IMAGE_CDN_HOST) {
    return path;
  }

  const extension = (fileType || getImagePathExtension(imageURL.pathname))
    .toLowerCase()
    .replace(/^\./, "");
  if (!RESIZABLE_IMAGE_EXTENSIONS.has(extension)) {
    return path;
  }

  const requestedFormat = imageURL.searchParams.get("f");
  const isOriginalImagePath = requestedFormat === "original";
  if (!originalWidth && !isOriginalImagePath) {
    return path;
  }

  const resizeWidth = getDisplayImageResizeWidth({
    displayHeight,
    displayWidth,
    originalWidth,
  });
  if (!resizeWidth) {
    return path;
  }

  const effectiveFormat = JPEG_IMAGE_FORMATS.has(requestedFormat || "")
    ? requestedFormat
    : extension;
  if (
    effectiveFormat === "png" &&
    (!originalWidth ||
      originalWidth < MIN_PNG_ORIGINAL_WIDTH_FOR_RESIZE_PX ||
      resizeWidth > MAX_PNG_DISPLAY_RESIZE_WIDTH_PX)
  ) {
    return path;
  }

  if (isOriginalImagePath) {
    imageURL.searchParams.delete("f");
  }
  imageURL.searchParams.set("d", `${resizeWidth}x`);
  return imageURL.toString();
};

export const getPageUrl = ({
  campaignPublicId,
  sitePublicId,
  campaignType,
}: {
  campaignType: CampaignType;
  campaignPublicId: string | null | undefined;
  sitePublicId: string | null | undefined;
}) => {
  const domain =
    campaignType === "PROMOTION"
      ? getPromotionPageBaseUrl()
      : campaignType === "FORM_EVENT"
      ? getEventPageBaseUrl()
      : campaignType === "FORM_SURVEY"
      ? getSurveyPageBaseUrl()
      : "";

  return `${domain}?s=${sitePublicId}&c=${campaignPublicId}`;
};

export const isValidUrl = (url: string) => {
  try {
    new URL(url);
    return true;
  } catch (e) {
    return false;
  }
};

export const extractPathnameAndParameters = (
  url: string
): {
  pathname: string;
  parameters: Record<string, string>;
} => {
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    const searchParams = Object.fromEntries(urlObj.searchParams.entries());
    return { pathname, parameters: searchParams };
  } catch (error) {
    const [pathname, ...parameters] = url.split("?");
    const searchParams = Object.fromEntries(
      new URLSearchParams(parameters.join("")).entries()
    );
    return {
      pathname: pathname ?? "",
      parameters: searchParams,
    };
  }
};
